import React, { useEffect, useRef, useState } from 'react';
import './HeroCarousel.css';
import { useContent } from '../../context/ContentContext';
import eventsUnnameableReading from '../../assets/carousel/events-unnameable-reading.webp';
import pedagogyClassroom from '../../assets/carousel/pedagogy-classroom.webp';

const HOLD_MS = 6000;

/* Used only when Airtable is unreachable, keyed by the names in fallbackContent. */
const bundledImages = {
  'carousel/events-unnameable-reading.webp': eventsUnnameableReading,
  'carousel/pedagogy-classroom.webp': pedagogyClassroom,
};

const wantsStillness = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/*
 * Drawn rather than typed: a ‹ glyph carries its own side bearings and sits on a
 * baseline, so it never quite lands in the middle of a round button. Both paths
 * are 7 units wide and 12 tall about the centre of the 24 unit box, which puts
 * the mark dead centre whatever size the button is. The two unit stroke comes out
 * at 1.5px on screen at the size the stylesheet gives the svg.
 */
const Chevron = ({ pointing }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d={pointing === 'left' ? 'M15.5 6 L8.5 12 L15.5 18' : 'M8.5 6 L15.5 12 L8.5 18'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/*
 * A band of photographs across the top of a page. The slides sit in a horizontal
 * scroller that snaps, so a phone swipes through them with the momentum its
 * browser gives any scrollable strip, while the arrows and dots scroll the same
 * strip for anyone using a mouse.
 */
const HeroCarousel = ({ page }) => {
  const { gallery } = useContent();
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const photos = gallery
    .filter((photo) => photo.page === page)
    .map((photo) => ({ ...photo, src: photo.imageUrl || bundledImages[photo.imageName] }))
    .filter((photo) => photo.src)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const count = photos.length;

  const showPhoto = (next) => {
    const track = trackRef.current;
    if (track) {
      track.scrollTo({ left: track.clientWidth * next, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (count < 2 || paused || wantsStillness()) {
      return undefined;
    }
    const timer = setInterval(() => {
      const track = trackRef.current;
      if (track) {
        const current = Math.round(track.scrollLeft / track.clientWidth);
        track.scrollTo({ left: track.clientWidth * ((current + 1) % count), behavior: 'smooth' });
      }
    }, HOLD_MS);
    return () => clearInterval(timer);
  }, [count, paused]);

  /*
   * A real listener rather than React's onScroll, which stopped reaching scroll
   * events in React 17, and passive so a swipe is never held up by this.
   */
  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }

    const onScroll = () => setIndex(Math.round(track.scrollLeft / track.clientWidth));

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, [count]);

  if (count === 0) {
    return null;
  }

  return (
    <div
      className="hero-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      // Once a finger has moved the strip, it stays where it was left rather
      // than being pulled onwards mid-swipe.
      onTouchStart={() => setPaused(true)}
    >
      <div className="hero-track" ref={trackRef}>
        {photos.map((photo, position) => (
          <div className="hero-slide" key={photo.id || photo.imageName}>
            <img
              src={photo.src}
              alt={photo.name}
              loading={position === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            className="hero-arrow hero-arrow-prev"
            aria-label="Previous photograph"
            onClick={() => showPhoto((index - 1 + count) % count)}
          >
            <Chevron pointing="left" />
          </button>
          <button
            type="button"
            className="hero-arrow hero-arrow-next"
            aria-label="Next photograph"
            onClick={() => showPhoto((index + 1) % count)}
          >
            <Chevron pointing="right" />
          </button>
          <div className="hero-dots">
            {photos.map((photo, position) => (
              <button
                type="button"
                key={photo.id || photo.imageName}
                className={position === index ? 'hero-dot hero-dot-on' : 'hero-dot'}
                aria-label={`Show photograph ${position + 1} of ${count}`}
                onClick={() => showPhoto(position)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroCarousel;
